import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { addComplaintEvidence } from "services/advocateComplaintService";
import { AdvocateCollectedEvidence, Complaints } from "types";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface EvidenceModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddEvidenceModal({
  isOpen,
  setIsOpen,
  complaintData,
  onRefresh,
}: EvidenceModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  // const [success, setSuccess] = useState(false);

  const [evidence, setEvidence] = useState<AdvocateCollectedEvidence>({
    collected_by: "",
    evidence_type: "",
    collected_date: "",
    remark: "",
    attachments: [],
    complaint: null,
    files: [] as File[],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setEvidence((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && evidence?.files?.length < 2) {
      setEvidence((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    // setError(null);
    // setSuccess(false);

    try {
      let formData = new FormData();

      Object.entries(evidence).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // If it's an array (e.g. multiple files)
          value.forEach((v, i) => {
            formData.append(`evidence_documents`, v);
          });
        } else {
          formData.append(key, value);
        }

        formData.append("complaint", complaintData?.uu_id?.toString() || "");
      });
      await addComplaintEvidence(formData);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      // setSuccess(true);
      setEvidence({
        collected_by: "",
        evidence_type: "",
        collected_date: "",
        remark: "",
        attachments: [],
        complaint: null,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
      // setError(
      //   err?.response?.data?.message || err.message || "Something went wrong."
      // );
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addEvidence")}>
      <div className="px-1">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("collectedBy")} <span className="text-red-600">*</span>
              </label>
              <input
                required
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                type="text"
                name="collected_by"
                placeholder={t("collectedByPlaceholder")}
                value={evidence.collected_by}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("evidenceType")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="evidence_type"
                  placeholder={t("evidenceTypePlaceholder")}
                  value={evidence.evidence_type}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("collectionDate")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="date"
                  name="collected_date"
                  value={evidence.collected_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("remark")}
              </label>
              <textarea
                rows={4}
                name="remark"
                value={evidence.remark}
                onChange={handleChange}
                placeholder={t("remarkPlaceholder")}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800 resize-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t("evidenceFiles")}
            </label>
            <div className="border border-gray-300 rounded p-4">
              <input
                type="file"
                ref={fileInputRef}
                name="files"
                onChange={handleFileChange}
                multiple
                style={{ display: "none" }}
                accept=".docx, .doc, .pdf, .png, .jpeg, .jpg"
              />

              <div className="text-center">
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  {t("uploadFiles")}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t("fileTypesSimple")}
                </p>
              </div>

              {evidence.files.length > 0 && (
                <div className="mt-4">
                  {evidence.files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                          <span className="text-red-600 text-xs font-bold">
                            {file.type.includes("pdf")
                              ? "PDF"
                              : file.type.includes("image")
                                ? "IMG"
                                : "FILE"}
                          </span>
                        </div>
                        <span className="text-sm text-gray-700 truncate">
                          {file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setEvidence((prev) => ({ ...prev, files: [] }))
                        }
                        className="text-gray-500 hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              {t("cancel")}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-800 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("submitting")}
                </>
              ) : (
                t("addEvidence")
              )}
            </button>
          </div>
        </form>
      </div>
    </SideModal>
  );
}
